import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { nuclearn_api_url } from "src/App";

// ===========================
// Types
// ===========================

export interface PartsAIRun {
  id: number;
  create_date: string;
  dialogue_ids: number[];
  create_user_fk: number;
  editing_part_fk: number | null;
}

export interface CreatePartsAIRunPayload {
  dialogue_id?: number;
}

// ===========================
// API Functions
// ===========================

/**
 * Create a new Parts AI run
 */
const createPartsAIRun = async (
  payload: CreatePartsAIRunPayload = {},
): Promise<PartsAIRun> => {
  const response = await axios.post(
    `${nuclearn_api_url}/products/parts/runs`,
    payload,
  );
  return response.data;
};

/**
 * Get a Parts AI run by ID
 */
const getPartsAIRunData = async (runId: number): Promise<PartsAIRun> => {
  const response = await axios.get(
    `${nuclearn_api_url}/products/parts/runs/${runId}`,
  );
  return response.data;
};

/**
 * Get Parts AI run data for a dialogue
 */
export const getPartsAIRunDataByDialogueId = async (
  dialogueId: number,
): Promise<PartsAIRun> => {
  const response = await axios.get(
    `${nuclearn_api_url}/products/parts/dialogues/${dialogueId}`,
  );
  return response.data;
};

// ===========================
// React Query Hooks
// ===========================

/**
 * Hook to create a Parts AI run
 */
export const useCreatePartsAIRun = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload?: CreatePartsAIRunPayload) => createPartsAIRun(payload || {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["partsAIRun"]);
      },
      onError: (error: any) => {
        console.error("Failed to create Parts AI run:", error);
      },
    },
  );
};

/**
 * Hook to get a Parts AI run
 */
export const useGetPartsAIRunData = (runId: number | undefined) => {
  return useQuery(["partsAIRun", runId], () => getPartsAIRunData(runId!), {
    enabled: !!runId,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to get Parts AI run data for a dialogue
 */
export const useGetPartsAIRunDataByDialogueId = (
  dialogueId: number | undefined,
) => {
  return useQuery(
    ["partsAIDialogue", dialogueId],
    () => getPartsAIRunDataByDialogueId(dialogueId!),
    {
      enabled: !!dialogueId,
      refetchOnWindowFocus: false,
    },
  );
};
